import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import {ApDocumentBase, AttachmentDto, AttachmentRequestConfig} from 'webcommon/shared';
import { ApBaseComponent } from '../../ap-base-component/index';
import { DocumentService, ViewportService } from '../../services/index';
import { ApFoxitViewerDirective } from './ap-foxit-viewer.directive';

@Component({
  selector: 'ap-sign-pdf',
  templateUrl: './ap-sign-pdf.component.html',
  styleUrls: ['./ap-sign-pdf.component.less'],
})
export class ApSignPdfComponent extends ApBaseComponent implements OnInit, OnChanges {
  static readonly ajsComponentName = 'apSignPdf';

  get document(): ApDocumentBase | null {
    return this.documentValue;
  }
  set document(val) {
    this.documentValue = val;
    if (val) {
      this.currentDocumentChange.emit(val);
    }
  }

  constructor(
    viewportService: ViewportService,
    private readonly documentService: DocumentService,
  ) {
    super();
    this.pageWidth = viewportService.getWidth();
    this.pageHeight = viewportService.getHeight();
  }

  private readonly pageWidth: number;
  private readonly pageHeight: number;
  @Input() disableFullscreen = false;
  @Input() disableInlinePdf?: boolean;
  @Input() documentId: string;
  @Input() loadDocument: (documentId: string, requestConfig: AttachmentRequestConfig) => Observable<AttachmentDto>;
  @Input() documentGetStaticUrl?: (documentId: string) => Observable<string>;
  @Input() documentLoad: (documentId: string, requestConfig: AttachmentRequestConfig) => Observable<AttachmentDto>;

  downloadingDocument = false;

  // ap-document-viewer
  private documentValue: ApDocumentBase | null = null;
  @Output() currentDocumentChange = new EventEmitter<ApDocumentBase>();

  loadError: any = null;
  loadingDocument = false;
  initialized = false;
  private currentAttachment: AttachmentDto;

  @ViewChild(ApFoxitViewerDirective, { static: true }) foxitViewer: ApFoxitViewerDirective<'sign'>;
  afterCreateRenderer: ApFoxitViewerDirective<'sign'>['afterCreate'] = (pdfRenderer) => {
    pdfRenderer.setupDragBehavior();
  };

  ngOnInit(): void {
    console.log('Document ID: ' + this.documentId);
    this.initialized = true;
    this.loadDocumentInternal(this.documentId, '', 1);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.initialized) {
      return;
    }
    const documentIdChanges = changes.documentId;
    if (documentIdChanges) {
      this.loadDocumentInternal(documentIdChanges.currentValue, documentIdChanges.previousValue, 1);
    }
  }

  loadDocumentInternal(documentId: string, previousDocumentId: string, page: number): Subscription {
    if (documentId !== previousDocumentId) {
      // dispose of previous document, since we are replacing it
      this.documentService.disposeDocument(this.document);
      this.loadError = null;
      this.document = null;
    }

    if (!documentId) {
      return Subscription.EMPTY;
    }

    this.loadingDocument = true;

    // Request double the device's resolution so we have a little give in the zooming department.
    // ^ this comment came from legacy ap-document-viewer in angularjs
    const request: AttachmentRequestConfig = {
      height: this.pageHeight * 2,
      page,
      width: this.pageWidth * 2,
    };

    const sub = this.loadDocument(documentId, request).pipe(
      finalize(() => this.documentLoadComplete()),
      takeUntil(this.onDestroy$),
    ).subscribe(
      (data) => this.documentLoadSuccess(data),
      (error: unknown) => this.documentLoadError(error),
    );
    return sub;
  }

  getAttachment() {
    return this.currentAttachment;
  }

  onDocumentLoadRef = (documentId: string, requestConfig: AttachmentRequestConfig) => {
    return this.onDocumentLoad(documentId, requestConfig);
  };

  private onDocumentLoad(documentId: string, requestConfig: AttachmentRequestConfig): Observable<AttachmentDto> {
    this.loadingDocument = true;
    const load$ = this.documentLoad(documentId, requestConfig).pipe(
      finalize(() => this.documentLoadComplete()),
    );
    return load$;
  }

  private documentLoadComplete() {
    this.loadingDocument = false;
  }

  private async documentLoadSuccess(attachment: AttachmentDto): Promise<void> {
    // dispose of previous document, since we are replacing it
    this.currentAttachment = attachment;
    this.documentService.disposeDocument(this.document);
    this.loadError = null;
    this.document = this.documentService.mapFromAttachment(attachment);

    const pdfRenderer = await this.foxitViewer.deferredPdfRenderer.resultPromise;
    if (pdfRenderer.attachmentsToSave.has(this.documentId)) {
      const existingData = pdfRenderer.attachmentsToSave.get(this.documentId);
      if (existingData) {
        // todo: move this attachment logic out of the renderer;
        //  it doesn't belong there, or even in this class
        pdfRenderer.openPDFData(existingData).then(() => {
          pdfRenderer.setAttachment(this.documentId);
        });
      }
    } else {
      pdfRenderer.openPDFData(attachment.Data).then(() => {
        pdfRenderer.setAttachment(this.documentId);
      });
    }
  }

  private documentLoadError(error: any) {
    this.loadError = error;
  }
}
